import {
  DialogModalDS,
  ButtonDS,
  InputDS,
  Spacer,
  NumericInputDS,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch } from "../../../../redux/hooks";
import styled from "styled-components";
import "../../../../i18n/i18n";
import { createPayoutAsync } from "../billingsSlice";

export const PayoutModal = (props: {
  isVisible: boolean;
  onCloseModal: () => void;
  balanceAvailable: number;
  iban: string;
  providerId: string;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { onCloseModal } = props;

  const [displayError, setDisplayError] = useState<boolean>(false);
  const [balanceInput, setBalanceInput] = useState<number | "">("");

  const isValidForm: boolean = useMemo(
    () =>
      balanceInput !== "" &&
      balanceInput > 0 &&
      balanceInput * 100 <= props.balanceAvailable,
    [balanceInput, props.balanceAvailable],
  );

  const resetValue = useCallback(() => {
    setDisplayError(false);
    setBalanceInput("");
  }, [setDisplayError]);

  const onSubmit = useCallback(() => {
    if (!balanceInput || !isValidForm) return;

    // We need to round to the nearest cent for numbers like 68.18 - if not, the conversion to cents will be 6818.000000000001 instead of 6818
    const amountInCents = Math.round(balanceInput * 100);

    void dispatch(
      createPayoutAsync({
        amount: amountInCents,
        providerId: props.providerId,
      }),
    );
    onCloseModal();
  }, [balanceInput, dispatch, isValidForm, onCloseModal, props.providerId]);

  const clickToSubmit = useCallback(() => {
    setDisplayError(true);
    isValidForm && onSubmit();
  }, [isValidForm, onSubmit]);

  useEffect(() => {
    if (props.isVisible === false) {
      resetValue();
    }
  }, [props.isVisible, resetValue]);

  const getBalanceError = useCallback(() => {
    if (!displayError) return undefined;
    if (balanceInput === 0 || balanceInput === "") {
      return t("account.createPayout.modal.balance.error.empty");
    } else if (balanceInput * 100 > props.balanceAvailable) {
      return t("account.createPayout.modal.balance.error.valid");
    } else return undefined;
  }, [balanceInput, displayError, props.balanceAvailable, t]);

  return (
    <DialogModalDS
      visible={props.isVisible}
      title={t("account.createPayout.modal.title") || ""}
      onClose={props.onCloseModal}
      bottomButtons={{
        leftButton: (
          <ButtonDS
            text={t("account.createPayout.modal.cancel")}
            format={"fill"}
            buttonType={"secondary"}
            onClick={() => {
              props.onCloseModal();
            }}
          />
        ),
        rightButton: (
          <ButtonDS
            text={t("account.createPayout.modal.add")}
            format={"fill"}
            buttonType={"primary"}
            onClick={clickToSubmit}
            disabled={!isValidForm}
          />
        ),
      }}
    >
      <StyledModalContent>
        <InputDS
          label={t("account.createPayout.modal.balanceAvailable")}
          value={props.balanceAvailable / 100 + " €"}
          withoutSpacerBottom
          isDisabled={true}
        />
        <Spacer y={1} />
        <NumericInputDS
          label={t("account.createPayout.modal.balance")}
          placeholder={t("account.createPayout.modal.placeholder")}
          value={balanceInput}
          withoutSpacerBottom
          update={(value) => setBalanceInput(value)}
          floatNumber
          maxDigitsAfterDecimal={2}
          onKeyDown={(e) => e.key === "Enter" && clickToSubmit()}
          error={getBalanceError()}
        />
        <Spacer y={1} />
        <InputDS
          label={t("account.createPayout.modal.iban")}
          value={props.iban}
          withoutSpacerBottom
          isDisabled={true}
        />
      </StyledModalContent>
    </DialogModalDS>
  );
};

const StyledModalContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
